import type { QueryClient } from '@tanstack/react-query';
import { useMultiCanvasViewStore } from '../stores/multiCanvasViewStore';
import { orgPlanningDependentQueryKeys } from '../domains/orgchart/hooks/invalidateOrgPlanningQueries';
import { orgModelQueryPrefix } from '../domains/orgchart/hooks/orgModelQueryKeys';
import { purgeFolderScopedQueries, refreshFolderScopedQueries } from './useFolderDataGrants';

export interface AccessPolicyInvalidationDto {
  readonly id: string;
  readonly folderId: string;
  readonly version: number;
  readonly cause:
    | 'folder_acl'
    | 'folder_data_grant'
    | 'principal_binding'
    | 'authoritative_reporting_graph'
    | 'source_authority'
    | 'entitlement'
    | 'federation_grant';
  readonly action: 'created' | 'updated' | 'revoked';
  readonly federationId: string | null;
  readonly policyVersion: string | null;
  readonly createdAt: string;
}

export function coalesceAccessPolicyInvalidations(
  events: readonly AccessPolicyInvalidationDto[],
): readonly AccessPolicyInvalidationDto[] {
  const latestGraphEventByFolder = new Map<string, AccessPolicyInvalidationDto>();
  const latestPolicyEventByFolder = new Map<string, AccessPolicyInvalidationDto>();
  events.forEach((event) => {
    if (event.cause === 'authoritative_reporting_graph') {
      const current = latestGraphEventByFolder.get(event.folderId);
      if (
        !current
        || event.version > current.version
        || (event.version === current.version && event.createdAt > current.createdAt)
        || (
          event.version === current.version
          && event.createdAt === current.createdAt
          && event.id > current.id
        )
      ) {
        latestGraphEventByFolder.set(event.folderId, event);
      }
      return;
    }
    const current = latestPolicyEventByFolder.get(event.folderId);
    if (supersedesPolicyEvent(event, current)) {
      latestPolicyEventByFolder.set(event.folderId, event);
    }
  });
  return events.filter((event) =>
    event.cause === 'authoritative_reporting_graph'
      ? latestGraphEventByFolder.get(event.folderId)?.id === event.id
      : latestPolicyEventByFolder.get(event.folderId)?.id === event.id);
}

/**
 * Every non-graph cause performs the same folder-scoped purge, so a burst of
 * them for one folder is N identical purges — and each purge resets the
 * folder's active queries, which refetches them. Collapsing the burst to one
 * event turns N refetch waves into one. A revoke is preferred over a
 * create/update in the same burst because it additionally clears selection,
 * which is the safe side to land on.
 */
function supersedesPolicyEvent(
  candidate: AccessPolicyInvalidationDto,
  current: AccessPolicyInvalidationDto | undefined,
): boolean {
  if (!current) return true;
  const candidateRevoked = candidate.action === 'revoked';
  const currentRevoked = current.action === 'revoked';
  if (candidateRevoked !== currentRevoked) return candidateRevoked;
  if (candidate.createdAt !== current.createdAt) return candidate.createdAt > current.createdAt;
  return candidate.id > current.id;
}

/**
 * One bounded inbox is shared by the poller and BroadcastChannel listener.
 * Marking an event as seen before it is applied prevents the local poll,
 * another tab's broadcast, and the local broadcast echo from multiplying the
 * same cache invalidation.
 */
export class AccessPolicyInvalidationInbox {
  private readonly seenIds = new Set<string>();
  private readonly seenOrder: string[] = [];
  private readonly highestGraphVersionByFolder = new Map<string, number>();
  private pending: AccessPolicyInvalidationDto[] = [];

  constructor(private readonly maxSeenIds = 2_000) {}

  enqueue(events: readonly AccessPolicyInvalidationDto[]): readonly AccessPolicyInvalidationDto[] {
    const accepted: AccessPolicyInvalidationDto[] = [];
    for (const event of coalesceAccessPolicyInvalidations(events)) {
      if (this.seenIds.has(event.id)) continue;
      if (event.cause === 'authoritative_reporting_graph') {
        const highestVersion = this.highestGraphVersionByFolder.get(event.folderId) ?? -1;
        if (event.version <= highestVersion) continue;
        this.highestGraphVersionByFolder.set(event.folderId, event.version);
      }
      this.seenIds.add(event.id);
      this.seenOrder.push(event.id);
      accepted.push(event);
      this.pending.push(event);
    }
    while (this.seenOrder.length > this.maxSeenIds) {
      const expired = this.seenOrder.shift();
      if (expired) this.seenIds.delete(expired);
    }
    return accepted;
  }

  drain(): readonly AccessPolicyInvalidationDto[] {
    const events = coalesceAccessPolicyInvalidations(this.pending);
    this.pending = [];
    return events;
  }

  retry(events: readonly AccessPolicyInvalidationDto[]): void {
    this.pending = [...events, ...this.pending];
  }

  hasPending(): boolean {
    return this.pending.length > 0;
  }
}

export async function applyAccessPolicyInvalidation(
  queryClient: QueryClient,
  event: AccessPolicyInvalidationDto,
): Promise<void> {
  if (event.cause === 'authoritative_reporting_graph') {
    const canonicalKey = orgModelQueryPrefix(event.folderId);
    // Scope/as-of/diagram variants converge independently. A fresh variant
    // cannot certify a sibling query's graph version.
    const canonicalRefresh = queryClient.invalidateQueries({
      queryKey: canonicalKey,
      predicate: (query) => {
        const data = query.state.data as { graphVersion?: unknown } | undefined;
        return typeof data?.graphVersion !== 'number' || data.graphVersion < event.version;
      },
      refetchType: 'active',
    });
    for (const queryKey of orgPlanningDependentQueryKeys(event.folderId)) {
      if (queryKey[0] === 'atlas' && queryKey[1] === 'org-model') continue;
      void queryClient.invalidateQueries({
        queryKey,
        refetchType: 'active',
      });
    }
    await canonicalRefresh;
    return;
  }

  if (event.action === 'revoked') {
    useMultiCanvasViewStore.getState().clearSelection();
  }
  if (event.cause === 'entitlement') {
    await purgeExactQueries(queryClient, (query) => query.queryKey[0] === 'entitlements');
    await invalidateAtlasAccessForFolder(queryClient, event.folderId);
    // An update can itself be a downgrade or add-on removal. Protected folder
    // projections must therefore be purged for every entitlement transition,
    // not only events explicitly labelled revoked.
    await purgeFolderScopedQueries(queryClient, event.folderId);
    return;
  }

  // ACL/grant/binding/source/federation events can change which protected
  // records or relationships exist in any folder projection. Folder scope is
  // therefore the narrowest safe boundary until publishers include record and
  // diagram targets. Do not additionally purge unrelated global pickers,
  // searches, assistants, exports, or other organizations by string match.
  //
  // A CREATED grant/ACL/binding only widens clearance, so nothing currently
  // rendered over-discloses. The hard purge resets active queries to loading
  // and unmounts whole folder views mid-interaction — a freshly created
  // folder's own provisioning event closed the open import dialog when the
  // feed delivered it (~60s later). Refresh in the background instead;
  // updates and revocations keep the fail-closed reset.
  if (event.action === 'created') {
    await refreshFolderScopedQueries(queryClient, event.folderId);
    return;
  }
  await purgeFolderScopedQueries(queryClient, event.folderId);
}

async function invalidateAtlasAccessForFolder(
  queryClient: QueryClient,
  folderId: string,
): Promise<void> {
  await purgeExactQueries(queryClient, (query) => query.queryKey[0] === 'atlas'
      && query.queryKey[1] === 'access'
      && query.queryKey[4] === folderId);
}

async function purgeExactQueries(
  queryClient: QueryClient,
  predicate: (query: { queryKey: readonly unknown[] }) => boolean,
): Promise<void> {
  await queryClient.cancelQueries({ predicate });
  queryClient.removeQueries({ predicate, type: 'inactive' });
  await queryClient.resetQueries({ predicate, type: 'active' });
}
